"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";
import type { ContentChannel, ContentProfileId } from "@/lib/agents/types";
import type { BrandLogoAsset } from "@/lib/planning/types";

type SidebarProfile = {
  id: ContentProfileId;
  name: string;
  brand: string;
  channel: ContentChannel;
};

const NAVIGATION = [
  { href: "/", label: "Visão geral", icon: "home" },
  { href: "/content", label: "Conteúdos", icon: "content" },
  { href: "/profiles", label: "Empresas e perfis", icon: "companies" },
  { href: "/prospecting", label: "Prospecção", icon: "prospecting" },
  { href: "/clients", label: "Clientes", icon: "clients" },
  { href: "/#team-title", label: "Agentes", icon: "agents" },
] as const;

// Na tela de login não há sessão ainda: mostrar a barra lateral revelaria a
// lista de marcas e perfis para quem nem entrou.
const BARE_ROUTES = ["/login"];

function initials(value: string): string {
  return value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

function NavigationIcon({ name }: { name: (typeof NAVIGATION)[number]["icon"] }) {
  const paths = {
    home: <><path d="M3 10.8 12 3l9 7.8" /><path d="M5.5 9.5V21h13V9.5M9 21v-7h6v7" /></>,
    content: <><rect x="3" y="4" width="18" height="16" rx="3" /><path d="M7 9h10M7 13h7M7 17h4" /></>,
    companies: <><path d="M4 21V7l8-4v18M12 9h8v12M8 8v.01M8 12v.01M8 16v.01M16 13v.01M16 17v.01M2 21h20" /></>,
    prospecting: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5M11 8v6M8 11h6" /></>,
    clients: <><path d="M3 7h18l-1.5 11a2 2 0 0 1-2 1.7H6.5a2 2 0 0 1-2-1.7z" /><path d="M8 7V5a4 4 0 0 1 8 0v2M9 13l2 2 4-4" /></>,
    agents: <><circle cx="9" cy="8" r="3" /><path d="M3.5 20v-2a5.5 5.5 0 0 1 11 0v2M16 4.5a3 3 0 0 1 0 5.5M17 13a5 5 0 0 1 3.5 4.8V20" /></>,
  } as const;
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
}

function isActive(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  if (href.startsWith("/#")) return false;
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function WorkspaceShell({ children, profiles }: { children: ReactNode; profiles: SidebarProfile[] }) {
  const pathname = usePathname();
  const [logos, setLogos] = useState<Record<string, BrandLogoAsset>>({});

  useEffect(() => {
    if (BARE_ROUTES.includes(pathname)) return;
    let active = true;
    function loadLogos() {
      return fetch("/api/planning-board?scope=logos", { cache: "no-store" })
      .then(
        (response) =>
          response.json() as Promise<{
            logos: Partial<Record<ContentProfileId, BrandLogoAsset>>;
          }>,
      )
      .then(({ logos: profileLogos }) => {
        if (!active) return;
        const next: Record<string, BrandLogoAsset> = {};
        for (const profile of profiles) {
          const logo = profileLogos[profile.id];
          if (logo && !next[profile.brand]) next[profile.brand] = logo;
        }
        setLogos(next);
      })
      .catch(() => undefined);
    }
    void loadLogos();
    window.addEventListener("pine:brand-logo-updated", loadLogos);
    return () => {
      active = false;
      window.removeEventListener("pine:brand-logo-updated", loadLogos);
    };
  }, [pathname, profiles]);

  if (BARE_ROUTES.includes(pathname)) return <>{children}</>;

  return (
    <div className="workspace-shell">
      <aside className="workspace-sidebar">
        <Link className="workspace-brand" href="/" aria-label="Pine Agents — visão geral">
          <span className="workspace-brand-symbol" aria-hidden="true">P</span>
          <span>Pine <em>Agents</em></span>
        </Link>

        <nav className="workspace-navigation" aria-label="Navegação principal">
          <p>Espaço de trabalho</p>
          {NAVIGATION.map((item) => (
            <Link
              className={isActive(pathname, item.href) ? "workspace-nav-link workspace-nav-link-active" : "workspace-nav-link"}
              href={item.href}
              key={item.href}
            >
              <NavigationIcon name={item.icon} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="sidebar-profiles">
          <div className="sidebar-section-heading">
            <p>Perfis editoriais</p>
            <Link href="/profiles" aria-label="Ver todos os perfis">{profiles.length}</Link>
          </div>
          <div className="sidebar-profile-list">
            {profiles.map((profile) => (
              <Link
                className={pathname.includes(profile.id) ? "sidebar-profile-link sidebar-profile-link-active" : "sidebar-profile-link"}
                href={`/profiles/${profile.id}/board`}
                key={profile.id}
                title={profile.name}
              >
                <span className={`profile-avatar profile-avatar-${profile.channel}`} aria-hidden="true">
                  {logos[profile.brand] ? (
                    <Image
                      src={`/api/local-media?job=${encodeURIComponent(logos[profile.brand].jobId)}&file=${encodeURIComponent(logos[profile.brand].mediaFile)}`}
                      alt=""
                      width={30}
                      height={30}
                      unoptimized
                    />
                  ) : initials(profile.brand)}
                </span>
                <span className="sidebar-profile-copy">
                  <strong>{profile.brand}</strong>
                  <small>{profile.channel === "instagram" ? "Instagram" : "LinkedIn"}</small>
                </span>
                <span className={`channel-dot channel-dot-${profile.channel}`} aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>

        <div className="workspace-status">
          <span aria-hidden="true" />
          <div><strong>Ambiente local</strong><small>Publicação sob aprovação</small></div>
        </div>
      </aside>

      <div className="workspace-content">
        <header className="workspace-mobile-header">
          <Link className="workspace-brand" href="/">
            <span className="workspace-brand-symbol" aria-hidden="true">P</span>
            <span>Pine <em>Agents</em></span>
          </Link>
          <Link className="mobile-companies-link" href="/profiles">Empresas</Link>
        </header>
        {children}
      </div>
    </div>
  );
}
